import * as THREE from 'three';
import {ViewOrientationMaterialFactory} from '../../material/ViewOrientationMaterialFactory';
import {getStandardViewDefinition} from './standardViewDefinitions';
import {StandardViewType, ViewOrientationTargetKind} from './standardViewTypes';
import {ViewOrientationElementMesh} from './ViewOrientationElementMesh';

/**
 * 边面网格。
 *
 * 设计说明：
 * 1. 该类一次只生成一个边面，禁止在内部批量创建 12 条边；
 * 2. 边面的几何、尺寸和装配逻辑全部收口到类内部，和主面类保持一致；
 * 3. 当前容器层是否挂载边面由外部决定，本类只保证单边语义与姿态正确。
 */
export class ViewOrientationEdgeMesh extends ViewOrientationElementMesh {

    /**
   * 构造单个边面 mesh。
   *
   * @param viewType 标准视图类型。
   * @throws 当视图定义不是边面类型时抛出错误。
   */
  public constructor(viewType: StandardViewType) {
    const definition = getStandardViewDefinition(viewType);
    if (definition.kind !== ViewOrientationTargetKind.Edge) {
      throw new Error(`边面类型错误：${viewType}`);
    }
    const metrics = ViewOrientationEdgeMesh.createMetrics();

    const material = ViewOrientationMaterialFactory.createBasicMaterial(definition.kind);

    super({
      viewType,
      kind: definition.kind,
      label: definition.label,
      geometry: ViewOrientationEdgeMesh.createEdgeGeometry(metrics),
      material,
      renderOrder: ViewOrientationElementMesh.RENDER_ORDER,
    });

    this.name = `view-orientation-edge-${viewType}`;
    this.applyEdgePlacement(viewType, metrics);
  }

  /**
   * 释放资源。
   */
  public dispose(): void {
    this.geometry.dispose();
    this.material.dispose();
  }

  /**
   * 创建边面尺寸参数。
   *
   * 设计约束：
   * 1. 连接面长边需和大面的上下左右直边等长；
   * 2. 连接面最短两条边需和大面的角边等长；
   * 3. Front + Top + FrontTop 这一组必须严格闭合，因此主面内缩量与切角量必须相同；
   * 4. 在此基础上复用主面的比例公式，统一推导连接面尺寸与偏移。
   *
   * @returns 尺寸参数。
   */
  private static createMetrics(): {
    edgeWidth: number;
    edgeHeight: number;
    positionOffset: number;
  } {
    const size = ViewOrientationElementMesh.SIZE;
    const outerHalfSize = size / 2;
    const edgeCut = size / (4 + 4 * Math.SQRT2);
    const faceLength = size - edgeCut * 2;
    const cornerEdgeLength = Math.SQRT2 * edgeCut;
    const straightEdgeLength = faceLength - edgeCut * 2;
    const edgeWidth = straightEdgeLength;
    const edgeHeight = cornerEdgeLength;
    const positionOffset = outerHalfSize - edgeCut / 2;

    return {
      edgeWidth,
      edgeHeight,
      positionOffset,
    };
  }

  /**
   * 创建单个边面几何。
   *
   * @param metrics 尺寸参数。
   * @returns PlaneGeometry。
   */
  private static createEdgeGeometry(metrics: {
    edgeWidth: number;
    edgeHeight: number;
    positionOffset: number;
  }): THREE.PlaneGeometry {
    void metrics.positionOffset;
    return new THREE.PlaneGeometry(metrics.edgeWidth, metrics.edgeHeight);
  }

  /**
   * 应用边面装配位置与朝向。
   *
   * @param viewType 标准视图类型。
   * @param metrics 尺寸参数。
   */
  private applyEdgePlacement(
    viewType: StandardViewType,
    metrics: {
      edgeWidth: number;
      edgeHeight: number;
      positionOffset: number;
    },
  ): void {
    void metrics.edgeWidth;
    void metrics.edgeHeight;

    const position = ViewOrientationEdgeMesh.resolveEdgePosition(viewType);
    this.position.set(
      position[0] * metrics.positionOffset,
      position[1] * metrics.positionOffset,
      position[2] * metrics.positionOffset,
    );

    const widthAxis = ViewOrientationEdgeMesh.resolveEdgeWidthAxis(viewType);
    const normalAxis = new THREE.Vector3(position[0], position[1], position[2]).normalize();
    const heightAxis = new THREE.Vector3().crossVectors(normalAxis, widthAxis).normalize();
    const basisMatrix = new THREE.Matrix4().makeBasis(widthAxis, heightAxis, normalAxis);

    this.quaternion.setFromRotationMatrix(basisMatrix);
  }

  /**
   * 解析边面对应的归一化位置。
   *
   * @param viewType 标准视图类型。
   * @returns 归一化位置。
   */
  private static resolveEdgePosition(
    viewType: StandardViewType,
  ): readonly [number, number, number] {
    switch (viewType) {
      case StandardViewType.FrontTop:
        return [0, 1, 1];
      case StandardViewType.FrontBottom:
        return [0, -1, 1];
      case StandardViewType.FrontRight:
        return [1, 0, 1];
      case StandardViewType.FrontLeft:
        return [-1, 0, 1];
      case StandardViewType.BackTop:
        return [0, 1, -1];
      case StandardViewType.BackBottom:
        return [0, -1, -1];
      case StandardViewType.BackRight:
        return [1, 0, -1];
      case StandardViewType.BackLeft:
        return [-1, 0, -1];
      case StandardViewType.TopRight:
        return [1, 1, 0];
      case StandardViewType.BottomRight:
        return [1, -1, 0];
      case StandardViewType.TopLeft:
        return [-1, 1, 0];
      case StandardViewType.BottomLeft:
        return [-1, -1, 0];
      default:
        throw new Error(`不支持的边面视图类型：${viewType}`);
    }
  }

  /**
   * 解析边面长边方向。
   *
   * @param viewType 标准视图类型。
   * @returns 长边方向向量。
   */
  private static resolveEdgeWidthAxis(viewType: StandardViewType): THREE.Vector3 {
    switch (viewType) {
      case StandardViewType.FrontTop:
      case StandardViewType.FrontBottom:
      case StandardViewType.BackTop:
      case StandardViewType.BackBottom:
        return new THREE.Vector3(1, 0, 0);
      case StandardViewType.FrontRight:
      case StandardViewType.FrontLeft:
      case StandardViewType.BackRight:
      case StandardViewType.BackLeft:
        return new THREE.Vector3(0, 1, 0);
      case StandardViewType.TopRight:
      case StandardViewType.BottomRight:
      case StandardViewType.TopLeft:
      case StandardViewType.BottomLeft:
        return new THREE.Vector3(0, 0, 1);
      default:
        throw new Error(`不支持的边面长边方向：${viewType}`);
    }
  }

}
